import { Reveal } from "@/components/bits";
import { ArrowRight, Sparkles } from "lucide-react";

const FOR_YOU = [
  "Ya disfrutás los libros pero querés leerlos con otros ojos y entender más allá.",
  "Sos una lectora que ama la literatura y busca profundidad sin academicismo.",
  "Sos una escritora en formación que necesita referentes y una comunidad de lectura.",
  "Querés entender por qué Pizarnik, Gallardo, Cabezón Cámara y Enríquez tienen, cada una a su manera, una visión y una voz tan especial.",
];

const BEFORE = [
  "Lectura superficial, sin pausa real frente a los textos.",
  "Atención dispersa: leés, pero algo siempre se te escapa.",
  "Las autoras que cambiaron la literatura argentina son apenas nombres lejanos.",
  "Una biblioteca interna que se llenó casi siempre de varones.",
];

const AFTER = [
  "Una mirada que funciona como prisma: lee el texto desde muchos ángulos a la vez.",
  "Capacidad de detenerte e iluminar pasajes que antes pasaban de largo.",
  "Pizarnik, Gallardo, Cabezón Cámara y Enríquez vivas en tu cabeza.",
  "Herramientas reales de análisis literario y crítico: sin academicismo, sin clases aburridas, sin bodoques de lectura innecesarios.",
];

export default function ParaQuien() {
  return (
    <section id="para-quien" data-testid="para-quien-section" className="bg-ink py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" /> Para quién
            </p>
            <h2 className="mt-8 font-serif text-3xl leading-[1.12] tracking-tight text-cream sm:text-4xl">
              Es para vos <span className="font-semibold not-italic text-gold">si...</span>
            </h2>
            <ul className="mt-10 space-y-5">
              {FOR_YOU.map((item, i) => (
                <li key={i} className="flex gap-4" data-testid={`para-quien-item-${i}`}>
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-crimson-light" />
                  <p className="text-sm font-light leading-relaxed text-bone/85 sm:text-base">{item}</p>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15} className="self-center">
            <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" /> Tu transformación
            </p>
            <h2 className="mt-8 font-serif text-3xl leading-[1.15] tracking-tight text-cream sm:text-4xl">
              De la lectura que sobrevuela a una mirada <span className="font-semibold not-italic text-gold">afilada</span> que descubre lo que
              antes no podías ver.
            </h2>
            <p className="mt-6 text-sm font-light leading-relaxed text-ash sm:text-base">
              Dos meses para pasar de leer en automático a tener una mirada aguda que ve mucho más y puede expresarlo
              con claridad.
            </p>
          </Reveal>
        </div>

        <div className="relative mt-20 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16">
          <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 lg:flex">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-crimson-light/50 bg-ink text-gold">
              <ArrowRight size={20} />
            </span>
            <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">2 meses</span>
          </div>

          <Reveal>
            <div className="relative h-full">
              <div
                className="pointer-events-none absolute -left-16 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-coal blur-3xl"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold/10 blur-3xl"
                aria-hidden="true"
              />
              <div
                data-testid="transformacion-antes"
                className="relative h-full border border-cream/15 bg-ink/90 p-8 backdrop-blur-sm sm:p-12"
              >
              <h3 className="font-serif text-2xl font-medium text-ash sm:text-3xl">Antes</h3>
              <p className="mt-1 text-xs font-light italic text-ash/70">Donde estás hoy</p>
              <ul className="mt-10 space-y-6 border-t border-cream/10 pt-8">
                {BEFORE.map((b, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="mt-0.5 text-ash/60">·</span>
                    <p className="text-sm font-light leading-relaxed text-ash">{b}</p>
                  </li>
                ))}
              </ul>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative h-full">
              <div
                className="pointer-events-none absolute -left-16 -top-16 h-72 w-72 rounded-full bg-crimson/40 blur-3xl"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute -bottom-16 -right-16 h-72 w-72 rounded-full bg-gold/20 blur-3xl"
                aria-hidden="true"
              />
              <div
                data-testid="transformacion-despues"
                className="relative h-full border border-gold/40 bg-ink/90 p-8 backdrop-blur-sm sm:p-12"
              >
              <h3 className="font-serif text-2xl font-medium text-gold sm:text-3xl">Después</h3>
              <p className="mt-1 text-xs font-light italic text-gold/70">Donde llegás</p>
              <ul className="mt-10 space-y-6 border-t border-gold/20 pt-8">
                {AFTER.map((a, i) => (
                  <li key={i} className="flex gap-4">
                    <Sparkles size={14} className="mt-1 shrink-0 text-crimson-light" />
                    <p className="text-sm font-light leading-relaxed text-bone">{a}</p>
                  </li>
                ))}
              </ul>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-16 text-center font-serif text-xl italic text-gold sm:text-2xl">
            La misma página se transforma cuando aprendés a leerla.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
